import React from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileText, ArrowRight, Check, Bot, GripVertical } from 'lucide-react';

// Componente de Card Individual (Draggable)
const OrderCard = ({ order, onPrint, onUpdateStatus, onComplete, showComplete, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Tratar endereço como objeto ou string (compatibilidade)
  const address = order.address || {};
  let addressStr = '';
  if (typeof address === 'string') {
    // Endereço veio como string (pedidos antigos ou outras fontes)
    addressStr = address;
  } else {
    // Endereço veio como objeto {street, neighborhood, city}
    addressStr = [address.street, address.neighborhood, address.city].filter(Boolean).join(', ');
  }
  // Fallback para delivery_zone_name se address vazio
  if (!addressStr && order.delivery_zone_name) {
    addressStr = order.delivery_zone_name;
  }
  const orderDate = new Date(order.created_at);
  const timeStr = orderDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dateStr = orderDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  // DEBUG: Log para verificar source (convertendo order_number para string para evitar erros)
  const orderNumberStr = String(order.order_number || '');
  if (orderNumberStr.startsWith('WA')) {
    console.log('🔍 DEBUG Order:', {
      order_number: order.order_number,
      customer_name: order.customer_name,
      source: order.source,
      hasSource: !!order.source,
      sourceType: typeof order.source,
      isWhatsapp: order.source === 'whatsapp'
    });
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="glass-card hover:bg-white/15 transition-all mb-3 relative group"
      data-testid={`kanban-order-card-${order.id}`}
      onClick={(e) => {
        // Só chama onClick se não clicou em um botão
        if (!e.target.closest('button')) {
          onClick && onClick(order);
        }
      }}
    >
      {/* Handle de Arrasto - Ícone discreto */}
      <div
        {...listeners}
        className="absolute left-2 top-2 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
        title="Arrastar para mudar status"
        data-testid={`kanban-drag-handle-${order.id}`}
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>

      <div className="pb-3 border-b border-white/10 pl-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-white text-base cursor-pointer">
                {order.customer_name || 'Cliente'}
              </h3>
              {order.source === 'whatsapp' && (
                <span 
                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 border border-green-500/30 rounded-md text-green-400 text-xs font-medium" 
                  title="Pedido feito pela IA no WhatsApp"
                >
                  <Bot className="w-3 h-3" />
                  IA
                </span>
              )}
            </div>
            <p className="text-sm text-gray-300 mt-1 cursor-pointer">
              Pedido #{order.order_number}
            </p>
            <p className="text-xs text-gray-400 mt-0.5 cursor-pointer">
              {dateStr} às {timeStr}
            </p>
          </div>
        </div>

        {/* Additional Info */}
        {addressStr && (
          <div className="mt-2 text-sm text-gray-300 cursor-pointer">
            <p className="line-clamp-1">📍 {addressStr}</p>
          </div>
        )}

        {order.table_number && (
          <div className="mt-2 text-sm text-gray-300 cursor-pointer">
            <p>🪑 Mesa {order.table_number}</p>
          </div>
        )}

        {/* Items Preview */}
        {order.items && order.items.length > 0 && (
          <div className="mt-2 space-y-1 cursor-pointer">
            {order.items.slice(0, 2).map((item, idx) => (
              <div key={idx} className="text-sm text-gray-300">
                {item.quantity}x {item.product_name || item.name || item.additional_name || 'Item'}
              </div>
            ))}
            {order.items.length > 2 && (
              <p className="text-xs text-gray-400 italic">
                + {order.items.length - 2} item(ns)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Order Footer */}
      <div className="pt-3 pl-8">
        <div className="flex items-center justify-between mb-3">
          <span className="text-lg font-bold text-white cursor-pointer">
            R$ {order.total?.toFixed(2) || '0.00'}
          </span>
          {/* PIX Payment Badge - suporta tanto 'pix' quanto 'pix_mp' */}
          {(order.payment_method === 'pix' || order.payment_method === 'pix_mp') && (order.payment_status === 'paid' || order.status_pagamento === 'approved') && (
            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-semibold rounded-full border border-green-500/30">
              ✓ PIX Pago
            </span>
          )}
          {(order.payment_method === 'pix' || order.payment_method === 'pix_mp') && order.payment_status !== 'paid' && order.status_pagamento !== 'approved' && (
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-semibold rounded-full border border-yellow-500/30">
              ⏳ Aguardando PIX
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrint(order);
            }}
            className="flex-1 px-3 py-2 border border-white/20 text-gray-300 rounded-lg text-sm font-medium hover:bg-white/10 hover:text-white transition-colors flex items-center justify-center gap-1"
            title="Imprimir pedido"
            data-testid={`kanban-print-${order.id}`}
          >
            <FileText size={14} />
            Imprimir
          </button>

          {showComplete ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onComplete(order.id);
              }}
              className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-1"
              data-testid={`kanban-complete-${order.id}`}
            >
              <Check size={14} />
              Concluir
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdateStatus(order.id);
              }}
              className="flex-1 px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1"
              data-testid={`kanban-advance-${order.id}`}
            >
              Avançar
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente de Coluna do Kanban
const KanbanColumn = ({ title, orders, color, onPrint, onUpdateStatus, onComplete, showComplete, onClick }) => {
  const bgColorClass = {
    orange: 'bg-orange-500/10 border-orange-500/30',
    blue: 'bg-emerald-500/10 border-emerald-500/30',
    green: 'bg-green-500/10 border-green-500/30',
  }[color];

  const headerColorClass = {
    orange: 'bg-orange-500',
    blue: 'bg-emerald-500',
    green: 'bg-green-500',
  }[color];

  return (
    <div className={`flex-1 min-w-[320px] rounded-xl border-2 ${bgColorClass} p-4`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-lg font-bold text-white px-4 py-2 rounded-lg ${headerColorClass}`}>
          {title}
        </h2>
        <span className="text-sm text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full">
          {orders.length}
        </span>
      </div>

      <SortableContext items={orders.map(o => o.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 min-h-[200px]" data-testid={`kanban-column-${color}`}>
          {orders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum pedido</p>
            </div>
          ) : (
            orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onPrint={onPrint}
                onUpdateStatus={() => onUpdateStatus(order.id)}
                onComplete={onComplete}
                showComplete={showComplete}
                onClick={onClick}
              />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Componente Principal do Kanban Board
const KanbanBoard = ({ orders, onPrint, onUpdateStatus, setOrders, onCardClick }) => {
  const [activeId, setActiveId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Separar pedidos por status
  const pendingOrders = orders.filter(o => o.status === 'pendente');
  const inProductionOrders = orders.filter(o => o.status === 'em_producao');
  const deliveryOrders = orders.filter(o => o.status === 'entrega');

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeOrder = orders.find(o => o.id === active.id);
    if (!activeOrder) {
      setActiveId(null);
      return;
    }

    // Determinar novo status baseado na coluna de destino
    const overOrder = orders.find(o => o.id === over.id);
    if (overOrder && activeOrder.status !== overOrder.status) {
      // Mover para a coluna do pedido sobre o qual foi solto
      onUpdateStatus(activeOrder.id, overOrder.status);
    }

    setActiveId(null);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeOrder = orders.find(o => o.id === active.id);
    const overOrder = orders.find(o => o.id === over.id);

    if (!activeOrder || !overOrder) return;

    if (activeOrder.status !== overOrder.status) {
      // Reordenar visualmente (otimista)
      const activeIndex = orders.findIndex(o => o.id === active.id);
      const overIndex = orders.findIndex(o => o.id === over.id);

      const newOrders = [...orders];
      newOrders[activeIndex] = { ...activeOrder, status: overOrder.status };

      setOrders(newOrders);
    }
  };

  const handleComplete = (orderId) => {
    // Marcar como 'pronto' - isso remove da visualização
    onUpdateStatus(orderId, 'pronto');
  };

  const handleAdvance = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const statusFlow = {
      'pendente': 'em_producao',
      'em_producao': 'entrega',
    };

    const nextStatus = statusFlow[order.status];
    if (nextStatus) {
      onUpdateStatus(orderId, nextStatus);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        <KanbanColumn
          title="Pendente"
          orders={pendingOrders}
          color="orange"
          onPrint={onPrint}
          onUpdateStatus={handleAdvance}
          showComplete={false}
          onClick={onCardClick}
        />
        <KanbanColumn
          title="Preparando"
          orders={inProductionOrders}
          color="blue"
          onPrint={onPrint}
          onUpdateStatus={handleAdvance}
          showComplete={false}
          onClick={onCardClick}
        />
        <KanbanColumn
          title="Entrega"
          orders={deliveryOrders}
          color="green"
          onPrint={onPrint}
          onComplete={handleComplete}
          showComplete={true}
          onClick={onCardClick}
        />
      </div>

      <DragOverlay>
        {activeId ? (
          <div className="glass-card opacity-80 cursor-grabbing">
            <div className="font-bold text-white">Movendo pedido...</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
